import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNews } from "@/hooks/content";
import { programs } from "@/mocks/programs";
import { videos } from "@/mocks/videos";

export default function SearchBar({ scrolled = false }: { scrolled?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { items: newsItems } = useNews();

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      // Focus the input when the panel opens
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery("");
    }
  }, [open]);

  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!q) return { news: [], programs: [], videos: [] };

    const news = newsItems
      .filter((a) => a.status === "published")
      .filter(
        (a) =>
          a.headline.toLowerCase().includes(q) ||
          (a.excerpt || "").toLowerCase().includes(q),
      )
      .slice(0, 4);

    const progs = programs
      .filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q),
      )
      .slice(0, 3);

    const vids = videos
      .filter(
        (v) =>
          v.title.toLowerCase().includes(q) ||
          v.description.toLowerCase().includes(q),
      )
      .slice(0, 3);

    return { news, programs: progs, videos: vids };
  }, [q, newsItems]);

  const hasResults =
    results.news.length > 0 || results.programs.length > 0 || results.videos.length > 0;

  const goTo = (path: string) => {
    setOpen(false);
    setQuery("");
    navigate(path);
  };

  const buttonBase = scrolled
    ? "text-foreground-700 hover:text-primary-600 hover:bg-primary-50/50"
    : "text-white/90 hover:text-white hover:bg-white/10";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`p-2 rounded-md transition-colors cursor-pointer ${buttonBase}`}
        aria-label={t("search.placeholder")}
      >
        <i className="ri-search-line text-lg"></i>
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-[320px] md:w-[420px] bg-white rounded-lg border border-background-200 shadow-lg z-50 overflow-hidden">
          {/* Input */}
          <div className="relative border-b border-background-100">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("search.placeholder")}
              className="w-full pl-10 pr-8 py-3 text-sm text-foreground-800 bg-white focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                aria-label="Clear"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-[420px] overflow-y-auto">
            {!q && (
              <p className="px-4 py-6 text-sm text-foreground-500 text-center">
                {t("search.placeholder")}
              </p>
            )}

            {q && !hasResults && (
              <p className="px-4 py-6 text-sm text-foreground-500 text-center">
                {t("search.noResults")}
              </p>
            )}

            {results.news.length > 0 && (
              <div className="py-2">
                <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-foreground-400">
                  {t("search.news")}
                </p>
                {results.news.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => goTo(`/news/${a.slug}`)}
                    className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={a.image} alt={a.headline} className="w-full h-full object-cover" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground-800 line-clamp-1">{a.headline}</span>
                      <span className="block text-xs text-foreground-500">{a.category}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {results.programs.length > 0 && (
              <div className="py-2 border-t border-background-50">
                <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-foreground-400">
                  {t("search.programs")}
                </p>
                {results.programs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => goTo(`/programs/${p.slug}`)}
                    className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground-800 line-clamp-1">{p.name}</span>
                      <span className="block text-xs text-foreground-500">{p.category}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {results.videos.length > 0 && (
              <div className="py-2 border-t border-background-50">
                <p className="px-4 py-1 text-xs font-semibold uppercase tracking-wider text-foreground-400">
                  {t("search.videos")}
                </p>
                {results.videos.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => goTo(`/videos/${v.slug}`)}
                    className="w-full flex items-start gap-3 px-4 py-2 text-left hover:bg-background-50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-foreground-800 line-clamp-1">{v.title}</span>
                      <span className="block text-xs text-foreground-500">{v.duration}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}